import React from "react";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import i18n from "./DropdownLanguage/i18n";
import { normalizeClientRecord } from "../helpers/clientManagementNormalize";
import "./UserManagementMaster.css";

class ClientManagementCard extends React.Component {
  getClientTypeLabel(clientType, t) {
    if (!clientType) {
      return "-";
    }
    const normalized = clientType.toLowerCase();
    if (normalized === "owner") {
      return t("ClientManagementScreenContent.Owner");
    }
    if (normalized === "management company") {
      return t("ClientManagementScreenContent.ManagementCompany");
    }
    return clientType;
  }

  getClientTypeBadgeClass(clientType) {
    if (!clientType) {
      return "um-role-badge";
    }
    const normalized = clientType.toLowerCase();
    if (normalized === "owner") {
      return "um-role-badge um-client-type-owner";
    }
    if (normalized === "management company") {
      return "um-role-badge um-client-type-mgmt";
    }
    return "um-role-badge";
  }

  getTotalPages() {
    const { pageSize, totalRecords } = this.props;
    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  getVisiblePages(currentPage, totalPages) {
    const maxButtons = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    const pages = [];
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    return pages;
  }

  handlePageChange(page) {
    const { setUpdatedState } = this.props;
    const totalPages = this.getTotalPages();
    if (page < 1 || page > totalPages) {
      return;
    }
    setUpdatedState(page);
  }

  renderListLabel(label, iconClass) {
    return (
      <span className="um-field-label um-list-label">
        <i className={`fa ${iconClass} um-list-label-icon`} aria-hidden="true"></i>
        <span>{label}</span>
      </span>
    );
  }

  renderField(label, value, iconClass, isArabic = false, extraClass = "", title = "", withDivider = false) {
    return (
      <div
        className={`um-field-cell ${extraClass} ${
          withDivider ? "um-field-with-divider" : ""
        }`}
      >
        {this.renderListLabel(label, iconClass)}
        <span
          className={`um-field-value ${isArabic ? "arabic" : ""}`}
          title={title || undefined}
        >
          {value || "-"}
        </span>
      </div>
    );
  }

  renderPagination() {
    const { currentPage, pageSize, totalRecords, t } = this.props;
    if (!totalRecords) {
      return null;
    }

    const totalPages = this.getTotalPages();
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const visiblePages = this.getVisiblePages(currentPage, totalPages);

    return (
      <div className="um-pagination">
        <div className="um-pagination-info">
          {t("ClientContactUserManagementContent.Showing")}{" "}
          <strong>{startRecord}</strong>{" "}
          {t("ClientContactUserManagementContent.To")}{" "}
          <strong>{endRecord}</strong>{" "}
          {t("ClientContactUserManagementContent.Of")}{" "}
          <strong>{totalRecords}</strong>
        </div>
        {totalPages > 1 ? (
          <div className="um-pagination-controls">
            <button
              type="button"
              className="um-page-btn"
              onClick={() => this.handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label={t("ClientContactUserManagementContent.Previous")}
            >
              <i className="fa fa-chevron-left" aria-hidden="true"></i>
            </button>
            {visiblePages.map((page) => (
              <button
                key={page}
                type="button"
                className={`um-page-btn ${currentPage === page ? "is-active" : ""}`}
                onClick={() => this.handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="um-page-btn"
              onClick={() => this.handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label={t("ClientContactUserManagementContent.Next")}
            >
              <i className="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  renderCardData() {
    const { arraylist, viewClick, editClick, deleteClient, showSidePane, t } = this.props;
    const isArabic = i18n.language === "arab";
    const fieldsClass = showSidePane
      ? "um-user-fields um-user-fields-4col um-user-fields-sidepane"
      : "um-user-fields um-user-fields-4col";

    return arraylist && arraylist.length
      ? arraylist.map((item) => {
          const client = normalizeClientRecord(item);
          return (
          <div key={client.clientId} className={`um-user-card${showSidePane ? " um-user-card-sidepane" : ""}`}>
            <div className="um-user-card-inner">
              <div className="um-user-card-row">
                <div className={fieldsClass}>
                  {this.renderField(
                    t("ShipManagementContent.CompanyName"),
                    client.companyName,
                    "fa-institution",
                    isArabic,
                    "um-field-name"
                  )}
                  {this.renderField(
                    t("ServicedByScreenContent.Email"),
                    client.mail,
                    "fa-envelope-o",
                    isArabic,
                    "um-field-email",
                    client.mail,
                    true
                  )}
                  {this.renderField(
                    t("ClientManagementScreenContent.Phone"),
                    client.telephone,
                    "fa-phone",
                    isArabic,
                    "um-field-phone",
                    "",
                    true
                  )}
                  <div className="um-field-cell um-field-role um-field-with-divider">
                    {this.renderListLabel(
                      t("ClientManagementScreenContent.Owner/ManagementCompany"),
                      "fa-briefcase"
                    )}
                    <span className={this.getClientTypeBadgeClass(client.clientType)}>
                      {this.getClientTypeLabel(client.clientType, t)}
                    </span>
                  </div>
                </div>
                <div className="um-user-actions um-user-actions-multi">
                  <button
                    type="button"
                    className="um-action-btn um-view-btn"
                    onClick={() => viewClick(client.clientId, client)}
                    aria-label={t("ClientManagementScreenContent.ViewDetails")}
                  >
                    <i className="fa fa-eye" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    className="um-action-btn"
                    onClick={() => editClick(client.clientId)}
                    aria-label={t("IntervelScreen.Edit")}
                  >
                    <i className="fa fa-pencil" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    className="um-action-btn um-delete-btn"
                    onClick={() => deleteClient(client)}
                    aria-label={t("content.Delete")}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
        })
      : null;
  }

  render() {
    const { arraylist, t } = this.props;

    return (
      <div className="user-module-master pb-1">
        {this.renderCardData()}
        {arraylist && arraylist.length ? (
          this.renderPagination()
        ) : (
          <div className="um-empty-state">
            <div>
              <i className="fa fa-institution" aria-hidden="true"></i>
            </div>
            <div>{t("ClientManagementScreenContent.NoRecordsFound")}</div>
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    showSidePane: state.SidePanReducer.sidePan || false,
  };
}

export default withTranslation()(
  withRouter(connect(mapStateToProps)(ClientManagementCard))
);
